<template>
  <div class="px-5 py-2">
    <div v-if="allowedComponents.includes('Holdings')">
      <v-alert
          color="info"
          icon="mdi-information"
          dense
          outlined
      >
        Starting 2/12/2022 Member Holdings became Holdings and now includes all admin accounts except for
        blackbox@upma.org and upma@upma.org.
      </v-alert>
      <v-card class="elevation-4 mt-8">
        <v-col>
          <v-row class="px-1 mt-n3" justify="space-around">
            <v-btn color="primary"
                   @click="toggleTemporarySnapshot"
                   justify="right"
                   class="mt-4 mr-4">{{ temporarySnapshotLabel }} Temporary Snapshot
            </v-btn>
          </v-row>
        </v-col>
        <v-progress-linear v-if="this.pendingState === 'loading'"
                           indeterminate
                           color="primary"
        />
        <v-data-table
            @click:row="seeTransactions"
            fixed-header
            :headers="vaultedTableHeaders"
            :footer-props="{
              itemsPerPageOptions: pagination.itemsPerPageOptions,
            }"
            height="calc(100vh - 330px)"
            :items="vaultSnaps"
            :server-items-length="totalItems"
            :options.sync="pagination"
            :loading="pendingState === 'loading'"
            style="cursor: pointer"
        >
          <!--            height="65vh"-->
          <template v-for="field in columns" v-slot:[`item.${field}`]="{ item }">
            <span :key="field">
              <span v-if="field.includes('difference')">
                <span :class="getValueClass(item[field])">+{{ formatNumber(item[field]) }}</span>
              </span>
              <span v-if="field.includes('snap')">{{ mixin_UTCToPretty(item[field]) }}</span>
              <span v-else-if="!field.includes('difference')">{{ formatNumber(item[field]) }}</span>
            </span>
          </template>
        </v-data-table>
      </v-card>
      <member-transactions-detail v-bind="{ endTimestamp, startTimestamp, transactionsBetween }"
                                  ref="memberTransactionsDetail"/>
      <ErrorDialog ref="errorDialog"/>

    </div>
  </div>
</template>

<script>
import ErrorDialog from "@/components/ErrorDialog";
import MemberTransactionsDetail from "@/components/reports/MemberTransactionsDetail";

export default {

  name: "Holdings",
  components: {ErrorDialog, MemberTransactionsDetail},
  data: () => ({
    allowedComponents: JSON.parse(localStorage.getItem('allowedComponents') || ''),
    pagination: {
      page: 1,
      itemsPerPage: 25,
      itemsPerPageOptions: [10, 25, 50, 100],
      sortBy: ['snap'],
      sortDesc: [true]
    },
    totalItems: 0,
    endTimestamp: '',
    startTimestamp: '',
    transactionsBetween: [],
    vaultSnaps: [],
    pendingState: 'display',
    vaultedTableHeaders: [
      {align: 'start', sortable: true, value: 'snap', text: 'Snapshot Timestamp', width: '220px'},
      {align: 'end', sortable: false, value: 'fiat_vault', text: 'USD'},
      {align: 'end', sortable: false, value: 'fiat_vault_difference', text: 'Δ'},
      {align: 'end', sortable: false, value: 'goldDollarVault', text: 'Gold'},
      {align: 'end', sortable: false, value: 'gold_oz_vault', text: '(Oz)'},
      {align: 'end', sortable: false, value: 'gold_oz_vault_difference', text: 'Δ'},
      {align: 'end', sortable: false, value: 'silver_oz_vault', text: 'Silver'},
      {align: 'end', sortable: false, value: 'silver_oz_vault_difference', text: 'Δ'},
      {align: 'end', sortable: false, value: 'goldback_vault', text: 'Goldback'},
      {align: 'end', sortable: false, value: 'goldback_vault_difference', text: 'Δ'},
      {align: 'end', sortable: false, value: 'goldDollarLease', text: 'Leased GD', width: '120px'},
      {align: 'end', sortable: false, value: 'gold_oz_lease', text: 'Leased (Oz)', width: '125px'},
      {align: 'end', sortable: false, value: 'gold_oz_lease_difference', text: 'Δ'},
      {align: 'end', sortable: false, value: 'silver_oz_lease', text: 'Leased SD', width: '110px'},
      {align: 'end', sortable: false, value: 'silver_oz_lease_difference', text: 'Δ'},
      {align: 'end', sortable: false, value: 'goldback_lease', text: 'Leased GB', width: '110px'},
      {align: 'end', sortable: false, value: 'goldback_lease_difference', text: 'Δ'},
      {align: 'end', sortable: false, value: 'goldDollarPawn', text: 'Pawned GD', width: '120px'},
      {align: 'end', sortable: false, value: 'gold_oz_pawn', text: 'Pawned (Oz)', width: '125px'},
      {align: 'end', sortable: false, value: 'gold_oz_pawn_difference', text: 'Δ'},
      {align: 'end', sortable: false, value: 'silver_oz_pawn', text: 'Pawned SD', width: '110px'},
      {align: 'end', sortable: false, value: 'silver_oz_pawn_difference', text: 'Δ'},
      {align: 'end', sortable: false, value: 'goldback_pawn', text: 'Pawned GB', width: '110px'},
      {align: 'end', sortable: false, value: 'goldback_pawn_difference', text: 'Δ'},
    ],
    withTemporarySnapshot: false,
  }),

  computed: {
    temporarySnapshotLabel() {
      return this.withTemporarySnapshot ? 'Remove' : 'Include';
    },
    columns() {
      return this.vaultedTableHeaders.map(hdr => hdr.value);
    },
  },

  mounted() {
    localStorage.title = 'Holdings';
  },

  watch: {
    pagination: {
      handler() {
        this.getSettledVaulted();
      },
      deep: true,
    },
  },

  methods: {
    formatNumber: (amount) => amount.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,"),
    getValueClass(value) {
      const numValue = parseFloat(value);
      if (numValue > 0) return 'positive';
      if (numValue < 0) return 'negative';
      return 'neutral';
    },

    toggleTemporarySnapshot: function () {
      this.withTemporarySnapshot = !this.withTemporarySnapshot;
      this.$nextTick(() => this.getSettledVaulted());
    },

    getSettledVaulted: async function () {
      this.pendingState = 'loading';
      try {
        const response = await this.$axios.get(`/admin/reports/vaulted`, {
          params: {
            withTempSnapshot: this.withTemporarySnapshot,
            page: this.pagination.page,
            perPage: this.pagination.itemsPerPage,
            sortBy: Array.isArray(this.pagination.sortBy) ? this.pagination.sortBy[0] : this.pagination.sortBy || 'snap',
            sortDesc: Array.isArray(this.pagination.sortDesc) ? this.pagination.sortDesc[0] : this.pagination.sortDesc || false,
          }
        });
        this.vaultSnaps = response.data.data;
        this.totalItems = response.data.total;
        for (let snap of this.vaultSnaps) {
          snap.goldDollarVault = (parseFloat(snap.gold_oz_vault) * 50).toFixed(2).toString();
          snap.goldDollarLease = (parseFloat(snap.gold_oz_lease) * 50).toFixed(2).toString();
          snap.goldDollarPawn = (parseFloat(snap.gold_oz_pawn) * 50).toFixed(2).toString();
        }
      } catch (error) {
        this.$refs.errorDialog.showError({
          errorMessage: "Failed to get Vaulted.",
          forDevelopers: error
        });
      } finally {
        this.pendingState = 'display';
      }
    },

    getTransactions: async function () {
      this.pendingState = 'loading';
      try {
        let res = await this.$axios.get(`/admin/reports/vaulted/transactions`, {
          params: {
            startTimestamp: this.startTimestamp,
            endTimestamp: this.endTimestamp
          }
        });
        // this.transactionsBetween = res.data;
        this.transactionsBetween = res.data.transactions;
        this.$refs.memberTransactionsDetail.toggleShown();
      } catch (e) {
        this.$refs.errorDialog.showError({
          errorMessage: "Failed to get Transactions.",
          forDevelopers: e
        });
      }
      this.pendingState = 'display';
    },
    seeTransactions: function (selected) {
      this.endTimestamp = selected.snap
      this.startTimestamp = selected.previous_snap
      this.getTransactions()
    },
  },

  created: async function () {
    await this.getSettledVaulted();
  },
}
</script>

<style scoped>
.positive {
  color: green;
}

.negative {
  color: red;
}

.neutral {
  color: grey;
}
</style>